import axios from 'axios';
import { API_CONFIG, API_MASTER_CONFIG, API_SCREEN_BUILDER_CONFIG, API_LEAD_SERVICE_CONFIG, API_AI_QUEST_DOC_CONFIG } from '../config/api.config';
import { tokenManager } from './token.manager';
import { errorHandler, resetSessionExpiredFlag } from './error.handler';

const httpClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

const httpClientMaster = axios.create({
  baseURL: API_MASTER_CONFIG.BASE_MASTER_URL,
  timeout: API_MASTER_CONFIG.TIMEOUT,
  headers: API_MASTER_CONFIG.HEADERS,
});

const httpClientScreenBuilder = axios.create({
  baseURL: API_SCREEN_BUILDER_CONFIG.BASE_SCREEN_BUILDER_URL,
  timeout: API_SCREEN_BUILDER_CONFIG.TIMEOUT,
  headers: API_SCREEN_BUILDER_CONFIG.HEADERS,
});

const httpClientLeadService = axios.create({
  baseURL: API_LEAD_SERVICE_CONFIG.BASE_LEAD_SERVICE_URL,
  timeout: API_LEAD_SERVICE_CONFIG.TIMEOUT,
  headers: API_LEAD_SERVICE_CONFIG.HEADERS,
});

const httpClientAiRendering = axios.create({
  baseURL: API_AI_QUEST_DOC_CONFIG.BASE_AI_QUEST_DOC_URL,
  timeout: API_AI_QUEST_DOC_CONFIG.TIMEOUT,
  headers: API_AI_QUEST_DOC_CONFIG.HEADERS,
});

const httpauditClient = axios.create({
  baseURL: API_CONFIG.AUDIT_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach(item => {
            searchParams.append(key, item);
          });
        } else if (value !== null && value !== undefined && value !== '') {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    }
  }
});

// Centralized response interceptor handler
const handleResponseError = async (error, clientInstance) => {
  const originalRequest = error.config;

  // Skip refresh logic for the refresh endpoint itself to prevent infinite loops
  if (originalRequest.url?.includes('/v1/auth/refresh')) {
    // tokenManager.clearTokens();
    // window.location.href = '/login';
    // return Promise.reject(error);
  }

  // Handle 401 - Token expired, attempt refresh
  if (error.response?.status === 401 && !originalRequest._retry) {
    // Check if this is a token validation error (not just expiry)
    const errorCode = error.response?.data?.errorCode;
    const errorMessage = error.response?.data?.message;
    
    // If token is invalid (not just expired), don't retry - force re-login
    if (errorCode === 'INVALID_TOKEN' || errorMessage?.includes('outdated role')) {
      tokenManager.clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    originalRequest._retry = true;

    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      // No refresh token available, can't refresh - reject the error
      return Promise.reject(error);
    }

    try {
      // Use a plain axios call without interceptors for refresh
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/refresh`, {
        refresh_token: refreshToken,
      });

      // Extract tokens from the response structure: data[0].access_token
      const tokenData = response.data.data?.[0];
      const newAccessToken = tokenData?.access_token;
      const newRefreshToken = tokenData?.refresh_token;

      if (!newAccessToken) {
        throw new Error('No access token received from refresh');
      }

      // Update both access token and refresh token in storage
      tokenManager.setToken(newAccessToken);
      if (newRefreshToken) {
        tokenManager.setRefreshToken(newRefreshToken);
      }
      // Update the failed request with new token and retry
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return clientInstance(originalRequest);
    } catch (refreshError) {
      // Refresh token failed or expired - reject the error
      return Promise.reject(refreshError);
    }
  }

  // Handle 403 - Forbidden/Session expired, redirect to login
  if (error.response?.status === 403) {
    tokenManager.clearTokens();
    // Show error only once via errorHandler
    errorHandler.handleError(error);
    // Redirect to login after a short delay to allow toast to be shown
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
    return Promise.reject(error);
  }

  return Promise.reject(errorHandler.handleError(error));
};

httpClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpClientMaster.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpauditClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpClient.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, httpClient)
);

httpClientMaster.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, httpClientMaster)
);

httpClientScreenBuilder.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpClientScreenBuilder.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, httpClientScreenBuilder)
);

httpClientLeadService.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpClientLeadService.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, httpClientLeadService)
);

httpauditClient.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, httpauditClient)
);

httpClientAiRendering.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpClientAiRendering.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, httpClientAiRendering)
);

export { httpClient, httpClientMaster, httpClientScreenBuilder, httpClientLeadService, httpauditClient, httpClientAiRendering };
